import { plainText } from './discord';
import { shortLap } from './lapTime';

/**
 * "On track now", in a Discord server's channel: while friends drive, one message (silent: nobody's phone buzzes)
 * says who's on track, with a "Watch" and a "Race with" button each; it changes as they do, and it goes when nobody
 * is (or, the last of them leaving with a best lap to post, it becomes that lap's message: worker/discordPosts.ts).
 * The server's room keeps it (worker/live.ts); what it says, and who's on it, is worked out here.
 */

/** On track this long before the message shows you (a quick look in and out shows nothing). */
export const SHOW_AFTER_MS = 20_000;
/** Off the track this long and you're off the message (back sooner, a phone put down a moment, and you stay). */
export const GRACE_MS = 15_000;
/** Laps and best times on the message change at most this often (who's on it changes at once). */
export const EDIT_EVERY_MS = 60_000;
/** Even who's on it changes at most this often (Discord allows five edits in five seconds per channel). */
export const EDIT_GAP_MS = 5_000;
/** "Left the track" waits this long to become the lap's message before it goes. */
export const LEFT_MS = 60_000;
/** The lap's message takes over the "on track" one only if fewer than this many messages came after it (else it goes at the bottom). */
export const KEPT_UNDER = 3;
/** Buttons for at most this many drivers (a row each: Discord allows five). */
const ROWS = 5;
/** Names on the message at most (then "and 3 more"). */
const LINES = 10;

/** A driver's time on track, as the room keeps it (one per player). */
export interface Stint {
  player: string;
  name: string;
  /** Their key in the room this visit (the buttons find them by it, or by name if they've come back since). */
  key: string;
  /** The channel their game was opened in (the message goes where their lap would: postChannel). */
  channel: string | null;
  /** On track since (a moment off it doesn't start a new stint). */
  since: number;
  /** Off the track since, or null: on it. */
  off: number | null;
  paused: boolean;
  best: number | null;
  /** Their game's switch: the channel may show them. */
  announce: boolean;
}

/** Who the message shows now: on track long enough (or only just off it), with their switch on, the first on first. */
export function onTrackNow(stints: Iterable<Stint>, now: number): Stint[] {
  return [...stints]
    .filter((s) => s.announce && now - s.since >= SHOW_AFTER_MS && (s.off === null || now - s.off <= GRACE_MS))
    .sort((a, b) => a.since - b.since || (a.player < b.player ? -1 : 1));
}

/** When the list may change next by itself: someone on track long enough, or off it too long. Null: never. */
export function nextChange(stints: Iterable<Stint>, now: number): number | null {
  let at = Infinity;
  for (const s of stints) {
    if (s.off !== null) at = Math.min(at, s.off + GRACE_MS + 1);
    else if (s.announce && now - s.since < SHOW_AFTER_MS) at = Math.min(at, s.since + SHOW_AFTER_MS);
  }
  return Number.isFinite(at) ? at : null;
}

interface Button {
  type: 2;
  style: 1 | 2;
  label: string;
  custom_id: string;
}
export interface LiveMessage {
  content: string;
  components: { type: 1; components: Button[] }[];
}

/** After a driver's name: paused, and their best today (never which lap: everyone restarts a lot). */
function status(s: Stint): string {
  const parts = [...(s.paused ? ['paused'] : []), ...(s.best !== null ? [`best ${shortLap(s.best)}`] : [])];
  return parts.map((p) => ` · ${p}`).join('');
}

/**
 * The message: one driver, "● **Sara** is on track now · best 33.104"; more, a line each under
 * "● **2 on track now**". Each driver's own row of buttons: Race with them (their car on your track as you race) and
 * Watch them.
 */
export function liveMessage(drivers: readonly Stint[]): LiveMessage {
  const lines =
    drivers.length === 1
      ? [`● **${plainText(drivers[0].name)}** is on track now${status(drivers[0])}`]
      : [
          `● **${drivers.length} on track now**`,
          ...drivers.slice(0, LINES).map((s) => `**${plainText(s.name)}**${status(s)}`),
          ...(drivers.length > LINES ? [`and ${drivers.length - LINES} more`] : []),
        ];
  return {
    content: lines.join('\n'),
    components: drivers.slice(0, ROWS).map((s) => ({
      type: 1 as const,
      components: [
        { type: 2 as const, style: 1 as const, label: `Race with ${s.name}`.slice(0, 80), custom_id: liveButtonId('race', s.key, s.name) },
        { type: 2 as const, style: 2 as const, label: `Watch ${s.name}`.slice(0, 80), custom_id: liveButtonId('watch', s.key, s.name) },
      ],
    })),
  };
}

/** The last driver gone, their lap's message on its way: it takes this one's place. */
export function leftMessage(name: string): LiveMessage {
  return { content: `**${plainText(name)}** has left the track`, components: [] };
}

/** What a tap on a button opens the game on: watching that driver, or racing with them. */
export interface LiveLaunch {
  mode: 'watch' | 'race';
  key: string;
  name: string;
}

const KEY = /^[0-9a-z]{6,12}$/;

/** A button's id: "live:r:<key>:<name>" (Discord allows 100 characters; the name is for a driver who's come back since). */
export function liveButtonId(mode: LiveLaunch['mode'], key: string, name: string): string {
  return `live:${mode === 'race' ? 'r' : 'w'}:${key}:${name}`.slice(0, 100);
}

/** A tapped button's launch, or undefined (not one of these). */
export function liveLaunchOf(customId: unknown): LiveLaunch | undefined {
  const m = typeof customId === 'string' ? /^live:([rw]):([0-9a-z]{6,12}):(.*)$/s.exec(customId) : null;
  if (!m) return undefined;
  return { mode: m[1] === 'r' ? 'race' : 'watch', key: m[2], name: m[3].slice(0, 40) };
}

/** A launch as the game hears it from its server (checked: it's only data). */
export function liveLaunch(v: unknown): LiveLaunch | null {
  const l = v as Partial<LiveLaunch> | null;
  if (!l || (l.mode !== 'watch' && l.mode !== 'race') || typeof l.key !== 'string' || !KEY.test(l.key) || typeof l.name !== 'string') return null;
  return { mode: l.mode, key: l.key, name: l.name.slice(0, 40) };
}
